import { createRef, Component } from 'react';
import { ReactSVG } from 'react-svg';
import Head from 'next/head';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import key from 'weak-key';
import { range } from 'lodash';
import Cookies from 'js-cookie';

import { get, getPluggerToken, getBlocksFromQueue } from '~/utils/utils';
import BlockParser from '~/components/blockParser/blockParser';
import Fields from '~/pluggerFields';
import PageWrapper from '~/components/wrappers/pageWrapper';
import PlugMaintenance from '~/components/plugMaintenance/plugMaintenance';
import SectionHero from '~/components/sectionHero/sectionHero';
import Share from '~/components/share/share';
import SiteSettings from '~/siteSettings';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });

/**
 * Plug
 * @category Page
 * @extends Component
 * @summary Display the plug page.
 * @return {Node} React node containing the plug page.
 */

class Plug extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      formState: 'form',
      formStateMessage: '',
      userIp: {},
      fields: {
        plug_id: props.data.plug && props.data.plug.id
          ? props.data.plug.id
          : '',
        ...Fields.map((field) => ({[field.name]: ''})).reduce((res, item) => {
          const k = Object.keys(item)[0];

          res[k] = item[k];

          return res;
        }, {}),
        ...props?.data?.plug?.social_media?.map?.((field) => ({[`social_media_${ field.id }`]: ''})).reduce((res, item) => {
          const k = Object.keys(item)[0];

          res[k] = item[k];

          return res;
        }, {})
      }
    };
    this.gatedMsg = 'Thank you for your interest. Based on the information that you have provided, we are, unfortunately, unable to accept your submission at this time.';
    this.form = createRef();
  }

  async componentDidMount() {
    // Get Age Gate
    const ageGate = Cookies.get('ageGate');

    if(ageGate) {
      this.setState({
        formState: 'error',
        formStateMessage: this.gatedMsg,
        loading: false
      });
    }

    // Get User Ip Data
    // const data = await fetch(`${ process.env.NEXT_PUBLIC_BASE_URL }/ip-info`, {
    //   method: 'GET',
    //   mode: 'cors'
    // });
    const data = {};
    if(data) {
      // const userIp = await data.json();
      const userIp = {"ip": "75.84.144.75, 167.82.171.24, 104.156.87.83, 10.22.81.79"};
      if(userIp.ip){
        this.setState({
          userIp: userIp.ip.split(',')[0]
        });
      }
    }
  }

  getRange(field) {
    let fieldRange = range(field.rangeStart, field.rangeEnd + 1);

    if(field.reverseRange) {
      fieldRange = fieldRange.reverse();
    }

    return fieldRange;
  }

  getFields() {
    const { fields, maxLengths } = this.state;
    const { plug }= this.props.data;
    const output = Fields.map((field) => {
      // Text Inputs
      if(field.type === 'text' || field.type === 'email' || field.type === 'phone' || field.type === 'zipcode') {
        return(
          <label
            key={`field-${key(field)}`}
            htmlFor={ field.name }
            className={ field.className }
          >
            <div className='plugger-form__label'>
              <span dangerouslySetInnerHTML={{__html: field.label}}></span>
              { field.required ? '' : ' (Optional)' }
            </div>
            <input
              id={ field.name }
              name={ field.name }
              onChange={ this.handleChange }
              required={ field.required }
              type={ field.type }
              value={ fields[field.name] }
            />
          </label>
        );
      }

      // Checkboxes
      if(field.type === 'checkbox') {
        return(
          <label
            key={`field-${key(field)}`}
            htmlFor={ field.name }
            className={ field.className }
          >
            <div className='field__checkbox'>
              <div>
                <input
                  id={ field.name }
                  name={ field.name }
                  onChange={ this.handleChange }
                  required={ field.required }
                  type={ field.type }
                  value={ fields[field.name] }
                />
              </div>
              <div>
                <span dangerouslySetInnerHTML={{__html: field.label}}></span>{ field.required ? '' : ' (Optional)' }
              </div>
            </div>
          </label>
        );
      }

      // Radios
      if(field.type === 'radio') {
        return(
          // eslint-disable-next-line jsx-a11y/label-has-for
          <label
            key={`field-${key(field)}`}
            htmlFor={ field.name }
            className={ field.className }
          >
            <div className='plugger-form__label' dangerouslySetInnerHTML={{__html: field.label}}></div>{ field.required ? '' : ' (Optional)' }
            <div className='field__radio'>
              <div>
                { field.options.map((option) => (
                  <div key={`field-radio-${key(option)}`} className='field__radio-option'>
                    <input
                      name={ field.name }
                      onChange={ this.handleChange }
                      required={ field.required }
                      type={ field.type }
                      value={ option.value }
                    />
                    { option.label }
                  </div>
                ))}
              </div>
            </div>
          </label>
        );
      }

      // Files
      if(field.type === 'file' && plug.allow_upload) {
        const uploadRequired = Object.prototype.hasOwnProperty.call(plug, 'upload_required')
          ? !!+(plug.upload_required)
          : false;

        return(
          <label
            key={`field-${key(field)}`}
            htmlFor={ field.name }
            className={ field.className }
          >
            <input
              id={ field.name }
              name={ field.name }
              onChange={ this.handleChange }
              required={ uploadRequired }
              type={ field.type }
            />
            <div className='field__file-btn'>
              <div className='btn btn--secondary'>
                <ReactSVG src='/img/icons/attach.svg' /> { field.label }
              </div>
              <div className='field__file-label'>
                { fields[field.name] ? fields[field.name].name : !uploadRequired ? 'Optional' : 'Required' }
              </div>
            </div>
          </label>
        );
      }

      // Text Areas
      if(field.type === 'textarea') {
        return(
          <label
            key={`field-${key(field)}`}
            htmlFor={ field.name }
            className={ field.className }
          >
            <div className='plugger-form__label'>
              <span dangerouslySetInnerHTML={{__html: field.label}}></span>
              { field.required ? '' : ' (Optional)' }
            </div>
            <textarea
              data-maxwords={ field.maxwords }
              id={ field.name }
              maxLength={ field.maxlength }
              name={ field.name }
              onChange={ this.handleChange }
              onPaste={ this.handleChange }
              required={ field.required }
              rows={ field.rows }
              value={ fields[field.name] }
            />
            <div className='field__maxwords'>
              { maxLengths && maxLengths[field.name] ? maxLengths[field.name] : 0 } / 250 words
            </div>
          </label>
        );
      }

      // Selects
      if(field.type === 'select' || field.type === 'select_range') {
        return(
          <label
            htmlFor={ field.name }
            className={ field.className }
            key={`field-${key(field)}`}
          >
            <div className='plugger-form__label'>
              <span dangerouslySetInnerHTML={{__html: field.label}}></span>
              { field.required ? '' : ' (Optional)' }
            </div>
            <div className='field__select'>
              <select
                id={ field.name }
                name={ field.name }
                onChange={ this.handleChange }
                required={ field.required }
                value={ fields[field.name] }
              >
                { field.type === 'select' && field.options.map((option) => (
                  <option key={`option-${key(option)}`} value={ option.value }>{ option.label }</option>
                ))}
                { field.type === 'select_range' &&
                  <option value={ field.firstItem.value }>{ field.firstItem.label }</option>
                }
                { field.type === 'select_range' && this.getRange(field).map((option) => (
                  <option value={ option } key={`option-${key({option})}`}>{ option }</option>
                ))}
              </select>
            </div>
          </label>
        );
      }

      // Sections
      if(field.type === 'section') {
        return(
          <div key={`field-${key(field)}`} className={ field.className }>
            <h3>{ field.heading }</h3>
            { field.desc && <p>{ field.desc }</p> }
          </div>
        );
      }

      // API Assigned Fields (Text Only ATM, Social Only)
      if(field.type === 'api') {
        const apiOutput = [];

        // Assign Heading
        if(field.heading && plug[field.key].length) {
          apiOutput.push(
            <div key={`field-${key(field)}`} className={ field.className }>
              <h3>{ field.heading }</h3>
              { field.desc && <p>{ field.desc }</p> }
            </div>
          );
        }

        // Assign Fields
        apiOutput.push(plug[field.key].map((apiField) => {
          const name = `social_media_${ apiField.id }`;

          return(
            <label
              key={`field-${key(apiField)}`}
              htmlFor={ name }
              className={ field.className }
            >
              <div className='plugger-form__label'>
                { apiField.label }
                { apiField.required ? '' : ' (Optional)' }
              </div>
              <input
                id={ name }
                name={ name }
                onChange={ this.handleChange }
                required={ apiField.required }
                type='text'
                value={ fields[name] }
              />
            </label>
          );
        }));

        return apiOutput;
      }

      return false;
    });

    return output;
  }

  submitForm = async e => {
    e.preventDefault();
    const { fields, userIp } = this.state;
    const { token, plugId } = this.props;
    const formValues = {
      ...fields,
      birthday: `${fields.birthday_year}-${fields.birthday_month}-${fields.birthday_day}`,
      ip_address: userIp,
      plug_id: plugId
    }

    // Form is valid
    if(this.form.current.reportValidity()) {

      // Check if user is 18
      if(!this.isAgeValid(fields.birthday_year, fields.birthday_month, fields.birthday_day)) {
        const expires = new Date(new Date().getTime() + 60 * 60 * 24 * 1000);
        Cookies.set('ageGate', true, { path: '/', expires });

        this.setState({
          formState: 'error',
          formStateMessage: this.gatedMsg,
          loading: false
        });

        return false;
      }

      // Set Loading State
      this.setState({
        loading: true
      });

      // Insert Values into Form Data
      const formData = new FormData();
      // eslint-disable-next-line no-restricted-syntax
      for (const k in formValues) {
        if (Object.prototype.hasOwnProperty.call(formValues,k)) {
          // Check for files
          if(formValues[k] && formValues[k].blob) {
            formData.append(k, formValues[k].blob, formValues[k].name);
          } else {
            formData.append(k, formValues[k]);
          }
        }
      }

      const data = await fetch(
        `${ process.env.NEXT_PUBLIC_PLUGGER_HOSTNAME }/api/1.0/submissions/add.json`,
        {
          method: 'POST',
          withCredentials: true,
          credentials: 'include',
          headers: {
            'Authorization': `bearer ${ token.pluggerToken.data.token }`,
          },
          body: formData
        }
      );

      if(data.status === 200) {
        this.setState({
          formState: 'success',
          loading: false
        });
      } else {
        this.setState({
          formState: 'error',
          formStateMessage: 'We are sorry, an unspecified error has occured, please try your submission again later.',
          loading: false
        });
      }
    }

    return false;
  }

  handleChange = e => {
    const { fields } = this.state;
    const optional = {};
    let { value } = e.target;
    const type = e.target.getAttribute('type');

    // If file
    if(type === 'file') {
      if(e.target.files[0]) {
        value = {
          name: e.target.files[0].name,
          blob: e.target.files[0]
        }
      }
    }

    // If Checkbox
    if(type === 'checkbox') {
      value = e.target.checked
        ? '1'
        : '0'
    }

    // If field has maxwords
    if(e.target.dataset.maxwords) {
      const words = e.target.value.split(' ');
      const wordsLength = words.length;
      optional.maxLengths = {
        [e.target.name]: wordsLength
      }

      // Trim Excess
      if(wordsLength > e.target.dataset.maxwords) {
        const trimmed = words.slice(0, e.target.dataset.maxwords);
        value = trimmed.join(' ');
        optional.maxLengths = {
          [e.target.name]: trimmed.length
        }
      }
    }

    this.setState({
      ...optional,
      fields: {
        ...fields,
        [e.target.name]: value
      }
    });
  }

  isAgeValid(year, month, day) {
    const now = parseInt(new Date().toISOString().slice(0, 10).replace(/-/g, ''), 10);
    const dob = year * 10000 + month * 100 + day * 1;

    return now - dob > 180000;
  }

  render() {
    const { router, sitePrimaryRsid, baseUrl, data, blocks } = this.props;
    const { formState, formStateMessage, loading } = this.state;

    if (data.plug && process.env.NEXT_PUBLIC_PLUGGER_ACTIVE !== '0') {
      const pageTitle = `${ data.plug.main_headline} | Share Your Story | ${ SiteSettings.siteName }`;
      const pageDesc = `${ data.plug.main_headline} - Drew wants to hear! Tell us your story.`;
      const path = get(router, 'query.plugId')
        ? `/share-your-story/plug/${ router.query.plugId.join('/') }`
        : '/';

      return (
        <>
          <Head>
            <title>{ pageTitle }</title>
            {/* Description */ }
            <meta name='description' content={ pageDesc } />
            <meta property='og:description' content={ pageDesc } />
            <meta name='twitter:description' content={ pageDesc } />
            {/* Title */ }
            <meta name='title' content={ pageTitle } />
            <meta property='og:title' content={ pageTitle } />
            <meta name='twitter:title' content={ pageTitle } />
            {/* Others */ }
            <meta property='og:url' content={ path } />
            <link rel='canonical' href={`${ baseUrl }${ path }`} />
            <meta name="keywords" content={ SiteSettings.defaultKeywords } />
          </Head>
          <SectionHero
            pageInfo={{
              img: {
                alt: 'The Drew Barrymore Show',
                sm: {
                  url: '/img/hero/4_mobile.jpg'
                },
                lg: {
                  url: '/img/hero/4.jpg'
                }
              }
            }}
            align='bottom'
          ></SectionHero>
          <div className='site-bg'>
            <div className='constrain constrain--bg'>
              <div className='content'>
                <div className='content__share'>
                  <Share
                    title={ pageTitle }
                  />
                </div>
                {/* Title */}
                <h1 className='content__title'>
                  The Drew Barrymore Show
                </h1>
                <div className='plug__ctas'>
                  <Link href='/share-your-story/plug/26'>
                    <a className='btn__cta btn__cta--muted btn__cta--with-icon'>
                      <span><ReactSVG src='/img/icons/plug.svg' /> share your story</span>
                    </a>
                  </Link>
                  <Link href='/share-your-story'>
                    <a className='btn__cta btn__cta--contrast'>
                      <span>choose your topic</span>
                    </a>
                  </Link>
                </div>
                <div className='plugger-form'>
                  <h2>{ data.plug.main_headline }</h2>
                  <div
                    className='plugger-form__desc'
                    dangerouslySetInnerHTML={{__html: data.plug.response_box_copy}}
                  ></div>
                  {formState === 'form' &&
                    <form ref={ this.form }>
                      <div className='plugger-fields grid'>
                        { this.getFields() }
                      </div>
                      <button className='btn__cta' type='button' onClick={ this.submitForm }>
                        <span>{ loading && <img src='/img/loader.gif' alt='Loading' /> } submit</span>
                      </button>
                    </form>
                  }
                  { formState === 'error' &&
                    <div className='text-center'>
                      <div className='plugger-notice plugger-notice--error'>
                        { formStateMessage }
                      </div>
                      <Link href='/'>
                        <a className='btn__cta'>
                          <span>Go To Homepage</span>
                        </a>
                      </Link>
                    </div>
                  }
                  { formState === 'success' &&
                    <div className='text-center'>
                      <div className='plugger-notice'>
                        Your message was sent successfully. Thanks!
                      </div>
                      <Link href='/'>
                        <a className='btn__cta'>
                          <span>Go To Homepage</span>
                        </a>
                      </Link>
                    </div>
                  }
                </div>
              </div>
            </div>
            <BlockParser blockItems={ blocks.entitySubQueueQuery.items } />
          </div>
          <Utag pageType='plugger' siteSection='share_your_story' data={{ sitePrimaryRsid }} />
          {/* @NOTE: For Form Validation Errors */}
          <style type='text/css' dangerouslySetInnerHTML={{__html: `
            html {
              scroll-padding-top: 100px;
            }
          `}} />
        </>
      );
    }

    return(
      <PlugMaintenance />
    );
  }
}

export async function getServerSideProps(ctx) {
  const token = await getPluggerToken();
  const plugId = ctx.params.plugId[0];
  let dataJson = {};

  if(token.pluggerToken.data.token) {
    const data = await fetch(
      `${ process.env.NEXT_PUBLIC_PLUGGER_HOSTNAME }/api/1.0/plugs/view/${ plugId }.json`,
      {
        method: 'GET',
        withCredentials: true,
        credentials: 'include',
        headers: {
          'Authorization': `bearer ${ token.pluggerToken.data.token }`
        }
      }
    );

    dataJson = await data.json();
  }

  // Get Blocks
  const blocks = await getBlocksFromQueue({
    target_id: 'plugs_blocks',
    limit: 100
  });

  return {
    props: {
      plugId,
      token,
      data: dataJson,
      blocks
    }
  }
}

export default PageWrapper(Plug);